import { ChooseAmountFormData } from '@src/components/sendMoneyFlow/components/amountStep';
import { MAXIMUM_AMOUNT_TO_SEND_UNVERIFIED_USER } from '@src/constants';
import * as yup from 'yup';

const stringSchema = yup.string().trim();

const amountFieldSchema = yup
  .number()
  .required('Please enter an amount')
  .typeError('Please enter a valid number');

interface IAmountSchemaFactoryOptions {
  type: 'send' | 'receive';
  currencyPath: string;
  minValue: number;
  isUserVerified?: boolean;
}

export function amountSchemaFactory({
  type,
  currencyPath,
  minValue,
  isUserVerified
}: IAmountSchemaFactoryOptions) {
  const minAmountErrorPrefix =
    type === 'send' ? 'The smallest amount you can send is' : 'The smallest amount they can get is';

  const maxAmountErrorPrefix = 'Oops. You can only send up to';

  return amountFieldSchema
    .test({
      name: 'min-amount',
      test: function (value) {
        const { path, createError, parent } = this;
        const currency = parent[currencyPath];
        const numberValue = value || 0;

        return numberValue >= minValue
          ? true
          : createError({
              message: `${minAmountErrorPrefix} ${minValue} ${currency}`.trim(),
              path
            });
      }
    })
    .test({
      name: 'max-amount',
      test: function (value) {
        const { path, createError, parent } = this;
        const formValues = parent as ChooseAmountFormData;
        const currency = parent[currencyPath];
        const maximumTransactionAmount = formValues.maximumTransactionAmount;
        const maxValue = isUserVerified
          ? maximumTransactionAmount
          : MAXIMUM_AMOUNT_TO_SEND_UNVERIFIED_USER;

        const numberValue = value || 0;
        const maxAmtFullErrorVerified = `Oops. You can only send up to ${maximumTransactionAmount} ${currency} per transaction.`;
        const maxAmtFullErrorUnverified = `Oops. You can only send up to ${MAXIMUM_AMOUNT_TO_SEND_UNVERIFIED_USER} ${currency} per transaction. To increase your limit, please verify your account.`;

        const errorType = !isUserVerified ? 'unverified' : 'verified';

        if (type === 'send' && (maxValue || maxValue === 0)) {
          return numberValue <= maxValue
            ? true
            : createError({
                message:
                  errorType === 'verified'
                    ? maxAmtFullErrorVerified
                    : errorType === 'unverified'
                    ? maxAmtFullErrorUnverified
                    : `${maxAmountErrorPrefix} ${maxValue} ${currency}`.trim(),
                path
              });
        }

        return true;
      }
    })
    .test({
      name: 'max-daily-amount',
      test: function (value) {
        const { path, createError, parent } = this;
        const formValues = parent as ChooseAmountFormData;
        const currency = parent[currencyPath];
        const remainingDailyTransactionAmount = formValues.remainingDailyTransactionAmount;

        const numberValue = value || 0;
        const maxAmtFullErrorVerified = `Oops. Your remaining daily limit is ${remainingDailyTransactionAmount} ${currency}. Kindly check your profile for more details.`;

        if (
          type === 'send' &&
          (remainingDailyTransactionAmount || remainingDailyTransactionAmount === 0)
        ) {
          return numberValue <= remainingDailyTransactionAmount
            ? true
            : createError({
                message: maxAmtFullErrorVerified,
                path
              });
        }

        return true;
      }
    })
    .test({
      name: 'max-monthly-amount',
      test: function (value) {
        const { path, createError, parent } = this;
        const formValues = parent as ChooseAmountFormData;
        const currency = parent[currencyPath];
        const remainingMonthlyTransactionAmount = formValues.remainingMonthlyTransactionAmount;

        const numberValue = value || 0;
        const maxAmtFullErrorVerified = `Oops. Your remaining monthly limit is ${remainingMonthlyTransactionAmount} ${currency}. Kindly check your profile for more details.`;

        if (
          type === 'send' &&
          (remainingMonthlyTransactionAmount || remainingMonthlyTransactionAmount === 0)
        ) {
          return numberValue <= remainingMonthlyTransactionAmount
            ? true
            : createError({
                message: maxAmtFullErrorVerified,
                path
              });
        }

        return true;
      }
    });
}

const chooseAmountSchema = {
  currency: stringSchema.required('Please choose a currency'),
  country: stringSchema.required('Please choose a country'),
  recipientType: stringSchema.required('Please select a payment delivery method'),
  rate: yup.object().required('Please wait while we get the new rate')
};

export default chooseAmountSchema;
